import styles from "../../../../assets/scss/common/modal/loginModal.module.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { isExistedUserAsync } from "../../../../store/login/loginSlice";
import { useAppDispatch, useModal, useUserAgent } from "../../../../hooks/hooks";
import { LoginStep } from "../LoginModal";
import { Timer } from "./Timer";

interface Props {
  email: string;
  incorrectCodeCount: number;
  setIncorrectCodeCount: (_: number) => void;
  setEmail: (_: string) => void;
  changeLoginStep: (_: LoginStep) => void;
  isBackAfterSendingCode: boolean;
  setIsBackAfterSendingCode: (_: boolean) => void;
}

export function EmailStep({
  email,
  incorrectCodeCount,
  setIncorrectCodeCount,
  setEmail,
  changeLoginStep,
  isBackAfterSendingCode,
  setIsBackAfterSendingCode,
}: Props) {
  const SEND_BLOCK_SEC = 30;
  const dispatch = useAppDispatch();
  const emailFormRef = useRef<HTMLFormElement>(null);
  const { openAlertModal } = useModal();

  const [alertMessage, setAlertMessage] = useState("");
  const [canClickSendBtn, setCanClickSendBtn] = useState(true);

  const isExistedUser = useCallback(
    async (emailAddr: string) => {
      try {
        const result = await dispatch(isExistedUserAsync(emailAddr)).unwrap();
        return result;
      } catch (error) {
        const errorObj: any = JSON.parse((error as Error).message);
        switch (errorObj?.errorCode?.httpCode) {
          case 477:
            setAlertMessage("해당 계정은 이미 탈퇴한 계정입니다.\n다른 이메일주소로 재시도해주세요.");
            break;
          case 472:
            setAlertMessage("현재 차단된 계정입니다.");
            break;
          default:
            openAlertModal(null);
        }

        throw error;
      }
    },
    [dispatch, openAlertModal]
  );

  const onClickSendingLoginCodeBtn = () => {
    if (canClickSendBtn) {
      const trimmedEmail = email.trim();
      if (trimmedEmail === "") {
        setAlertMessage("이메일을 입력해주세요.");
        return;
      }

      const isValidEmailForm = (emailFormRef.current as HTMLFormElement).reportValidity();
      if (!isValidEmailForm) {
        setAlertMessage("잘못된 형식의 이메일주소입니다. 다시 입력해주세요!");
        return;
      }

      isExistedUser(trimmedEmail).then(({ result }: { result: boolean }) => {
        if (result) {
          changeLoginStep(LoginStep.AUTH_WAY);
        } else {
          setAlertMessage("가입되지 않은 이메일입니다. 회원가입 후 이용해주세요!");
        }
      });
    }
  };

  useEffect(() => {
    if (!isBackAfterSendingCode) {
      return;
    }

    setCanClickSendBtn(false);

    if (incorrectCodeCount >= 5) {
      setAlertMessage("인증코드를 5회 잘못입력하셨습니다. 잠시후 시도해주세요.");
    } else {
      setAlertMessage("잠시후 시도해주세요.");
    }
  }, [isBackAfterSendingCode, incorrectCodeCount]);

  const doAtTheEndTimer = useCallback(() => {
    setCanClickSendBtn(true);
    setIncorrectCodeCount(0);
    setAlertMessage("");
    setIsBackAfterSendingCode(false);
  }, [setIncorrectCodeCount, setIsBackAfterSendingCode]);

  const { isDesktop } = useUserAgent();

  return (
    <div className={styles.textDesc}>
      <div className={styles.desc}>
        {isDesktop ? (
          <>
            <span className={styles.accent}>K-PASS with 앱에서 가입한 이메일을 입력해주세요!</span>
            <br />
            회원가입은 K-PASS with 앱에서 진행해주세요.
          </>
        ) : (
          <>
            <span className={styles.accent}>
              K-PASS with 앱에서 가입한
              <br />
              이메일을 입력해주세요!
            </span>
            <br />
            회원가입은 K-PASS with 앱에서
            <br /> 진행해주세요.
          </>
        )}
      </div>
      <form className={styles.emailForm} onSubmit={(e) => e.preventDefault()} ref={emailFormRef}>
        <input
          type="email"
          className={styles.input}
          value={email}
          name="email"
          onInvalid={(e) => e.preventDefault()}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={"with 앱에서 가입한 이메일 입력"}
        ></input>
        {canClickSendBtn ? (
          <></>
        ) : (
          <Timer
            initialMilliseconds={SEND_BLOCK_SEC * 1000}
            styleClassName={styles.timer}
            doAtTheEnd={doAtTheEndTimer}
          />
        )}
        {alertMessage ? <span className={styles.invalidEmailAddr}>{alertMessage}</span> : <></>}
        <button className={styles.button} onClick={onClickSendingLoginCodeBtn}>
          다음
        </button>
      </form>
    </div>
  );
}
